import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { todayISO } from "@/lib/days";
import { formatCZK, formatDate, plur } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TabSwitcher, useIsMobile } from "@/components/shared/PagePrimitives";
import { FilterPanel } from "@/components/shared/FilterPanel";
import { PaymentsDialog } from "@/components/shared/PaymentsDialog";
import { FileText, ReceiptText, ChevronLeft, ChevronRight, CheckCircle2, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { payableTotal, canSeeIssued } from "@/lib/documents";
import { useAuth } from "@/context/AuthContext";
import { autoPill } from "@/components/shared/Pill";

// Firemní pohled na úhrady: co je potřeba zaplatit, co inkasovat a kdy.
// Odchozí platby = přijaté faktury (/documents), příchozí = vydané faktury
// klientům (/issued-invoices). Sjednocený tvar platby nese `direction`.

const dayMs = 86400000;
const today0 = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const daysDiff = (iso) => Math.round((new Date(iso + "T00:00:00") - today0()) / dayMs);
// K úhradě = u konečné faktury jen doplatek po zálohách (171, payableTotal).
const restOf = (d) => Math.max(payableTotal(d) - (d.paid_total || 0), 0);
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

// Chipy v režimu výběru (154): Vše · Splatné dnes · Po splatnosti — filtrují viditelný seznam.
const SEL_CHIPS = [["all", "Vše"], ["today", "Splatné dnes"], ["overdue", "Po splatnosti"]];
export const chipFilter = (list, f) => (f === "all" ? list
  : list.filter((d) => d.due_date && (f === "today" ? daysDiff(d.due_date) === 0 : daysDiff(d.due_date) < 0)));

// Saldo počítá obě strany. Dobropisy (167b/A5): přijatý přidává k Inkasovat,
// vydaný přidává k Zaplatit.
export function computeSaldo({ payables = [], receivables = [], creditIn = [], creditOut = [] }) {
  const sum = (list) => list.reduce((s, d) => s + restOf(d), 0);
  const overdue = (list) => list.filter((d) => d.due_date && daysDiff(d.due_date) < 0).reduce((s, d) => s + restOf(d), 0);
  const pay = sum(payables) + sum(creditOut), rec = sum(receivables) + sum(creditIn);
  return { pay, rec, payOverdue: overdue(payables), recOverdue: overdue(receivables),
           balance: rec - pay, total: pay + rec };
}

export default function EconomicsPayments() {
  const [docs, setDocs] = useState([]);
  const [issued, setIssued] = useState([]);
  const [sub, setSub] = useState("prehled");
  const [payDoc, setPayDoc] = useState(null);
  const [recvInv, setRecvInv] = useState(null);   // vydaná faktura v dialogu příjmu
  const [dirF, setDirF] = useState("all");        // Vše / Odchozí / Příchozí
  const [calMonth, setCalMonth] = useState(() => todayISO().slice(0, 7));
  const mobile = useIsMobile();
  const [panelOpen, setPanelOpen] = useState(false);
  // Filtry „Provedených plateb" (157/D) — přežijí přepnutí podstránky (sessionStorage).
  const [filters, setFilters] = useState(() => {
    try { return { from: "", to: "", parties: {}, methods: {}, ...JSON.parse(sessionStorage.getItem("payments_filters") || "{}") }; }
    catch { return { from: "", to: "", parties: {}, methods: {} }; }
  });
  useEffect(() => { try { sessionStorage.setItem("payments_filters", JSON.stringify(filters)); } catch { /* ignore */ } }, [filters]);

  const { user } = useAuth();
  const issuedOk = canSeeIssued(user);   // MANAGER by na /issued-invoices dostal 403
  const load = useCallback(() => {
    // 220: lehký seznam dokladů — Platby čtou jen hlavičky a úhrady, ne položky.
    api.get("/documents?view=list").then((r) => setDocs(r.data)).catch(() => {});
    if (issuedOk) api.get("/issued-invoices").then((r) => setIssued(r.data)).catch(() => {});
  }, [issuedOk]);
  useEffect(() => { load(); }, [load]);
  // 341: `?pay=<id>` z Přehledu (Co hoří → Zaplatit) — po načtení dokladů rovnou otevře zápis úhrady.
  const [params, setParams] = useSearchParams();
  const payParam = params.get("pay");
  useEffect(() => {
    if (!payParam || !docs.length) return;
    const d = docs.find((x) => x.id === payParam);
    setParams((p) => { p.delete("pay"); return p; }, { replace: true });
    if (d) setPayDoc(d); else toast.error("Doklad k zaplacení jsme nenašli.");
  }, [payParam, docs, setParams]);
  useEffect(() => {
    const onSaved = () => load();
    window.addEventListener("pp-doc-saved", onSaved);
    return () => window.removeEventListener("pp-doc-saved", onSaved);
  }, [load]);

  const payables = useMemo(() => docs
    .filter((d) => restOf(d) > 0.5 && !d.is_credit_note)
    .sort((a, b) => String(a.due_date || "9999").localeCompare(String(b.due_date || "9999"))), [docs]);

  const receivables = useMemo(() => issued
    .filter((d) => restOf(d) > 0.5 && !d.is_credit_note)
    .sort((a, b) => String(a.due_date || "9999").localeCompare(String(b.due_date || "9999"))), [issued]);

  // Dobropisy (167): přijatý = vratka od dodavatele (pod Inkasovat), vydaný =
  // vratka klientovi (pod Zaplatit). Peníze zpět, nebo zápočet proti jiné faktuře.
  const creditIn = useMemo(() => docs.filter((d) => d.is_credit_note && restOf(d) > 0.5), [docs]);
  const creditOut = useMemo(() => issued.filter((d) => d.is_credit_note && restOf(d) > 0.5), [issued]);
  const [offset, setOffset] = useState(null);
  const recordRefund = async (credit, direction) => {
    const url = direction === "in" ? `/documents/${credit.id}/payments` : `/issued-invoices/${credit.id}/payments`;
    try {
      await api.post(url, { date: todayIso(), amount: restOf(credit), method: "Vratka",
        note: direction === "in" ? "vratka od dodavatele" : "vratka klientovi" });
      toast.success(direction === "in" ? "Vratka od dodavatele přijata." : "Vratka klientovi zaplacena.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saldo = useMemo(() => computeSaldo({ payables, receivables, creditIn, creditOut }), [payables, receivables, creditIn, creditOut]);

  // Sjednocený tvar platby pro seznam i kalendář. Dobropis (167b/B1): vratka
  // přijatého dobropisu je příjem, vratka vydaného je výdaj. Zápočet (method
  // „Zápočet") nehýbe penězi → `isOffset`, vyloučen ze VŠECH součtů.
  const payments = useMemo(() => {
    const out = [];
    docs.forEach((d) => (d.payments || []).forEach((p) => out.push({
      id: p.id, direction: d.is_credit_note ? "in" : "out", date: p.date, counterparty: d.supplier_name || "Bez názvu",
      document_label: `${d.is_credit_note ? "dobropis" : "faktura"} č. ${d.invoice_number || "—"}${d.project_name ? ` · ${d.project_name}` : ""}`,
      document_id: d.id, kind: "faktura", method: p.method || "—", note: p.note || "", amount: p.amount || 0,
      invoice_number: d.invoice_number || null, isOffset: p.method === "Zápočet",
    })));
    issued.forEach((d) => (d.payments || []).forEach((p) => out.push({
      id: p.id, direction: d.is_credit_note ? "out" : "in", date: p.date, counterparty: d.client_name || "Bez názvu",
      document_label: `${d.is_credit_note ? "dobropis" : "vydaná faktura"} č. ${d.invoice_number || "—"}${d.project_name ? ` · ${d.project_name}` : ""}`,
      document_id: d.id, kind: "faktura", method: p.method || "—", note: p.note || "", amount: p.amount || 0,
      invoice_number: d.invoice_number || null, isOffset: p.method === "Zápočet",
    })));
    return out.sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
  }, [docs, issued]);

  // Základ pro počty ve Filtrech = jen směrový filtr (Vše/Odchozí/Příchozí).
  const dirPayments = useMemo(
    () => (dirF === "all" ? payments : payments.filter((p) => p.direction === dirF)), [payments, dirF]);

  const monthEnd = (m) => { const [y, mm] = m.split("-").map(Number); const d = new Date(y, mm, 0); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
  const monthLabel = (m) => { const [y, mm] = m.split("-"); return `${MONTHS[Number(mm) - 1].toLowerCase()} ${y}`; };
  const monthsInData = useMemo(() => {
    const set = new Set();
    payments.forEach((p) => (p.date || "").length >= 7 && set.add(p.date.slice(0, 7)));
    return [...set].sort().reverse();
  }, [payments]);
  const monthValue = monthsInData.find((m) => filters.from === `${m}-01` && filters.to === monthEnd(m)) || "";

  const partyOptions = useMemo(() => {
    const seen = new Set();
    dirPayments.forEach((p) => seen.add(p.counterparty));
    return [...seen].sort((a, b) => a.localeCompare(b, "cs")).map((name) => ({ value: name, label: name }));
  }, [dirPayments]);
  const partyCounts = useMemo(() => {
    const m = {}; dirPayments.forEach((p) => { m[p.counterparty] = (m[p.counterparty] || 0) + 1; }); return m;
  }, [dirPayments]);
  const methodCounts = useMemo(() => {
    const m = {}; dirPayments.forEach((p) => { m[p.method] = (m[p.method] || 0) + 1; }); return m;
  }, [dirPayments]);
  const METHOD_OPTS = ["Převodem", "Hotově", "Kartou", "ručně"];

  // Provedené platby (157/D): směr + Měsíc/Období + Protistrana + Způsob.
  const shownPayments = useMemo(() => {
    let list = dirPayments;
    if (filters.from) list = list.filter((p) => (p.date || "") >= filters.from);
    if (filters.to) list = list.filter((p) => (p.date || "") <= filters.to);
    const parties = Object.keys(filters.parties || {}).filter((k) => filters.parties[k]);
    if (parties.length) list = list.filter((p) => parties.includes(p.counterparty));
    const methods = Object.keys(filters.methods || {}).filter((k) => filters.methods[k]);
    if (methods.length) list = list.filter((p) => methods.includes(p.method));
    return list;
  }, [dirPayments, filters]);
  // 401: ve Vše zvlášť příchozí a odchozí — jeden součet míchal, co nám přišlo a co jsme zaplatili
  const shownSum = useMemo(() => {
    const by = (dir) => shownPayments.filter((p) => !p.isOffset && p.direction === dir).reduce((s, p) => s + (p.amount || 0), 0);
    return { in: by("in"), out: by("out") };
  }, [shownPayments]);
  const shownSumText = dirF === "all" ? `přišlo ${formatCZK(shownSum.in)} · odešlo ${formatCZK(shownSum.out)}` : formatCZK(dirF === "in" ? shownSum.in : shownSum.out);

  const activeCount = [!!filters.from || !!filters.to,
    Object.values(filters.parties || {}).some(Boolean),
    Object.values(filters.methods || {}).some(Boolean)].filter(Boolean).length;
  const clearFilters = () => setFilters({ from: "", to: "", parties: {}, methods: {} });
  const setFilterValue = (key, val) => {
    if (key === "month") {
      setFilters((f) => (monthsInData.find((m) => f.from === `${m}-01` && f.to === monthEnd(m)) === val
        ? { ...f, from: "", to: "" }
        : { ...f, from: `${val}-01`, to: monthEnd(val) }));
    } else if (key === "from" || key === "to") {
      setFilters((f) => ({ ...f, [key]: val }));
    } else if (key === "parties" || key === "methods") {
      setFilters((f) => { const n = { ...(f[key] || {}) }; if (n[val]) delete n[val]; else n[val] = true; return { ...f, [key]: n }; });
    }
  };
  const filterGroups = [
    { key: "month", label: "Měsíc", layout: "pills", options: monthsInData.map((m) => ({ value: m, label: monthLabel(m) })) },
    { key: "period", label: "Období", layout: "range", fromKey: "from", toKey: "to" },
    { key: "parties", label: "Protistrana", layout: "checklist", searchable: true, options: partyOptions },
    { key: "methods", label: "Způsob", layout: "checklist", options: METHOD_OPTS.map((m) => ({ value: m, label: m })) },
  ];

  const payAll = async (d) => {
    const rest = restOf(d);
    try {
      await api.post(`/documents/${d.id}/payments`, {
        date: todayISO(), amount: rest, method: "Převodem", note: "doplaceno" });   // 401: místní den, ne UTC
      toast.success("Faktura je plně uhrazena.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="payments-page">
      <TabSwitcher value={sub} onChange={setSub} tabs={[
        { value: "prehled", label: "Přehled", testid: "pay-subtab-overview" },
        { value: "kalendar", label: "Kalendář", testid: "pay-subtab-calendar" },
      ]} />

      {sub === "prehled" ? (
        <>
          <SaldoBar saldo={saldo} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <BulkCard direction="out" title="Zaplatit" list={payables} testid="payables-list" onDone={load}
              credits={creditOut} onRefund={recordRefund} onOffset={(c) => setOffset({ credit: c, coll: "issued_invoices" })}
              meta={`${plur(payables.length, "doklad", "doklady", "dokladů")} · ${formatCZK(saldo.pay)}`}
              empty="Všechno zaplaceno."
              renderRow={(d, sel) => {
                const rest = restOf(d);
                const dd = d.due_date ? daysDiff(d.due_date) : null;
                const overdue = dd != null && dd < 0;
                return (
                  <PayRow key={d.id} testid={`payable-${d.id}`} overdue={overdue} sel={sel}>
                    <div className="min-w-0 flex-1">
                      <div className="font-heading font-bold truncate">{d.supplier_name || "Bez názvu"}</div>
                      <div className="text-[13px] text-muted-foreground truncate">
                        č. {d.invoice_number || "—"} · {d.project_name || "Režie"}{d.due_date ? ` · splatnost ${formatDate(d.due_date)}` : ""}
                      </div>
                      {d.paid_total > 0 && (
                        <div className="text-xs text-muted-foreground tabular-nums">
                          uhrazeno {formatCZK(d.paid_total)} z {formatCZK(d.total_with_vat)}
                        </div>
                      )}
                    </div>
                    <div className="sm:text-right shrink-0">
                      <div className="font-heading font-extrabold tabular-nums">{formatCZK(rest)}</div>
                      <DueHint dd={dd} overdue={overdue} />
                    </div>
                    {!sel.selMode && (
                      <div className="flex gap-2 shrink-0">
                        <Button size="sm" onClick={() => payAll(d)} className="h-9 rounded-full font-bold" data-testid={`pay-all-${d.id}`}>Zaplatit celé</Button>
                        <Button size="sm" variant="outline" onClick={() => setPayDoc(d)} className="h-9 rounded-full font-bold" data-testid={`pay-part-${d.id}`}>Část</Button>
                      </div>
                    )}
                  </PayRow>
                );
              }} />

            <BulkCard direction="in" title="Inkasovat" list={receivables} testid="receivables-list" onDone={load}
              credits={creditIn} onRefund={recordRefund} onOffset={(c) => setOffset({ credit: c, coll: "documents" })}
              meta={`${plur(receivables.length, "vydaná faktura", "vydané faktury", "vydaných faktur")} · ${formatCZK(saldo.rec)}`}
              empty="Nic k inkasu."
              renderRow={(d, sel) => {
                const rest = restOf(d);
                const dd = d.due_date ? daysDiff(d.due_date) : null;
                const overdue = dd != null && dd < 0;
                const due = payableTotal(d);
                const pct = due > 0 ? Math.min(Math.round(((d.paid_total || 0) / due) * 100), 100) : 0;
                return (
                  <PayRow key={d.id} testid={`receivable-${d.id}`} overdue={overdue} sel={sel}>
                    <div className="min-w-0 flex-1">
                      <div className="font-heading font-bold truncate">{d.client_name || "Bez názvu"}</div>
                      <div className="text-[13px] text-muted-foreground truncate">
                        č. {d.invoice_number || "—"} · {d.project_name || "Bez zakázky"}{d.due_date ? ` · splatnost ${formatDate(d.due_date)}` : ""}
                      </div>
                      {d.advances_deducted_gross > 0 && (
                        <div className="text-xs text-muted-foreground tabular-nums" data-testid={`receivable-adv-${d.id}`}>
                          celkem {formatCZK(d.total_with_vat || d.total_without_vat)} · zálohy −{formatCZK(d.advances_deducted_gross)}
                        </div>
                      )}
                      {d.paid_total > 0 && (
                        <div className="mt-1">
                          <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-success" style={{ width: `${pct}%` }} /></div>
                          <div className="text-xs text-muted-foreground mt-1 tabular-nums">
                            přijato {formatCZK(d.paid_total)} z {formatCZK(due)}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="sm:text-right shrink-0">
                      <div className="font-heading font-extrabold tabular-nums">{formatCZK(rest)}</div>
                      <DueHint dd={dd} overdue={overdue} />
                    </div>
                    {!sel.selMode && (
                      <Button size="sm" variant="outline" onClick={() => setRecvInv(d)} className="h-9 rounded-full font-bold shrink-0" data-testid={`receive-${d.id}`}>Zapsat příjem</Button>
                    )}
                  </PayRow>
                );
              }} />
          </div>

          <Card title="Provedené platby" meta={`${plur(shownPayments.length, "platba", "platby", "plateb")} · ${shownSumText}`} testid="payments-list"
            action={
              <div className="flex items-center gap-2">
                <div ref={autoPill} className="flex gap-1 p-1 rounded-full bg-secondary" data-testid="filter-direction">
                  {[["all", "Vše"], ["out", "Odchozí"], ["in", "Příchozí"]].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => setDirF(v)} data-testid={`filter-direction-${v}`} data-pill-on={dirF === v || undefined}
                      className={`h-8 px-3 rounded-full text-[13px] font-bold transition-colors ${dirF === v ? "bg-card shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
                  ))}
                </div>
                <button type="button" onClick={() => setPanelOpen((o) => !o)} data-testid="filters-btn"
                  className={`h-11 px-4 gap-2 bg-card rounded-full font-bold box-border inline-flex items-center border transition-colors ${activeCount ? "border-brand text-brand" : "border-border text-foreground hover:text-brand"}`}>
                  <SlidersHorizontal size={16} /> Filtry
                  {activeCount > 0 && <span className="rounded-full bg-brand text-white px-2 text-[12.5px] font-bold tabular-nums" data-testid="filters-btn-count">{activeCount}</span>}
                </button>
              </div>
            }>
            {panelOpen && (
              <FilterPanel open={panelOpen} onClose={() => setPanelOpen(false)} mobile={mobile} width={680}
                filterCount={activeCount} resultCount={shownPayments.length}
                plural={(n) => plur(n, "platba", "platby", "plateb")}
                values={{ month: monthValue, from: filters.from, to: filters.to, parties: filters.parties, methods: filters.methods }}
                counts={{ parties: partyCounts, methods: methodCounts }}
                setValue={setFilterValue} clear={clearFilters} groups={filterGroups} />
            )}
            {shownPayments.length === 0 ? <Empty>Zatím žádná platba.</Empty> : (
              <div className="overflow-x-auto">
                <div className="min-w-[860px]">
                  <div className="grid grid-cols-[120px_minmax(0,1.4fr)_minmax(0,1.6fr)_130px_150px] gap-3 pb-2 border-b border-border text-[12px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
                    <span>Datum</span><span>Protistrana</span><span>Doklad</span><span>Způsob</span><span className="text-right">Částka</span>
                  </div>
                  {shownPayments.map((p) => {
                    const Icon = p.kind === "uctenka" ? ReceiptText : FileText;
                    const inc = p.direction === "in";
                    return (
                      <div key={`${p.document_id}-${p.id}`} data-testid={`payment-row-${p.id}`}
                        role="button" tabIndex={0}
                        onClick={() => (inc ? setRecvInv(issued.find((d) => d.id === p.document_id) || null)
                          : setPayDoc(docs.find((d) => d.id === p.document_id) || null))}
                        onKeyDown={(e) => { if (e.key === "Enter") (inc ? setRecvInv(issued.find((d) => d.id === p.document_id) || null) : setPayDoc(docs.find((d) => d.id === p.document_id) || null)); }}
                        className={`grid grid-cols-[120px_minmax(0,1.4fr)_minmax(0,1.6fr)_130px_150px] gap-3 py-3 border-b border-border last:border-b-0 items-center cursor-pointer hover:bg-secondary/50 transition-colors ${inc ? "bg-success/[0.03]" : ""}`}>
                        <span className="text-muted-foreground tabular-nums">{formatDate(p.date)}</span>
                        <span className="flex items-center gap-2 min-w-0">
                          <Icon size={17} className={`shrink-0 ${inc ? "text-success" : "text-brand"}`} />
                          <span className="min-w-0">
                            <span className="block font-semibold truncate">{p.counterparty}</span>
                            <span className="block text-[12px] font-bold text-muted-foreground">{inc ? "příchozí" : "odchozí"}</span>
                          </span>
                        </span>
                        <span className="truncate text-muted-foreground flex items-center gap-1.5 min-w-0">
                          <span className="truncate">{p.document_label}</span>
                          {p.isOffset && (
                            <span className="shrink-0 bg-secondary rounded-full px-2 text-[11px] font-bold leading-5" data-testid={`payment-offset-pill-${p.id}`}>zápočet</span>
                          )}
                          {(p.note || "").startsWith("hromadně") && (
                            <span className="shrink-0 bg-secondary rounded-full px-2 text-[11px] font-bold leading-5" data-testid={`payment-bulk-pill-${p.id}`}>hromadně</span>
                          )}
                        </span>
                        <span className="truncate">{p.method}</span>
                        <span className={`text-right font-heading font-extrabold tabular-nums ${p.isOffset ? "text-muted-foreground" : inc ? "text-success" : ""}`}>
                          {p.isOffset ? "" : inc ? "+" : "−"}{formatCZK(p.amount)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Card>
        </>
      ) : (
        <PaymentsCalendar month={calMonth} setMonth={setCalMonth} payments={payments} />
      )}

      <PaymentsDialog doc={payDoc} onClose={() => setPayDoc(null)}
        onChanged={(updated) => { setPayDoc(updated); load(); }} />

      <PaymentsDialog doc={recvInv} direction="in" onClose={() => setRecvInv(null)}
        onChanged={(updated) => { setRecvInv(updated); load(); }} />

      <OffsetDialog offset={offset} docs={docs} issued={issued}
        onClose={() => setOffset(null)} onDone={() => { setOffset(null); load(); }} />
    </div>
  );
}

function Card({ title, meta, action, children, testid }) {
  return (
    <section className="bg-card border border-border rounded-[20px] p-5 shadow-[0_8px_24px_rgba(0,0,0,0.06)]" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <h2 className="font-heading font-extrabold text-[17px]">{title}</h2>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-muted-foreground tabular-nums">{meta}</span>
          {action}
        </div>
      </div>
      {children}
    </section>
  );
}

const Empty = ({ children }) => <p className="text-sm text-muted-foreground py-2">{children}</p>;

function DueHint({ dd, overdue }) {
  if (dd == null) return null;
  return (
    <div className={`text-xs font-bold ${overdue ? "text-destructive" : "text-amber-600 dark:text-amber-400"}`}>
      {overdue ? `${plur(-dd, "den", "dny", "dní")} po splatnosti` : dd === 0 ? "splatnost dnes" : `splatnost za ${plur(dd, "den", "dny", "dní")}`}
    </div>
  );
}

// Řádek seznamu; v režimu výběru (154) je celý klikací a vlevo má checkbox.
function PayRow({ testid, overdue, sel, children }) {
  const { selMode, checked, toggle } = sel;
  return (
    <div data-testid={testid} role={selMode ? "button" : undefined} tabIndex={selMode ? 0 : undefined}
      onClick={selMode ? toggle : undefined}
      onKeyDown={selMode ? (e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); toggle(); } } : undefined}
      aria-pressed={selMode ? checked : undefined}
      className={`border-b border-border last:border-b-0 py-3 flex flex-col sm:flex-row sm:items-center gap-3 ${overdue ? "bg-destructive/[0.03]" : ""} ${selMode ? "cursor-pointer -mx-2 px-2 rounded-[10px] hover:bg-secondary/50" : ""} ${selMode && checked ? "bg-brand/[0.06]" : ""}`}>
      {selMode && <Checkbox checked={checked} className="h-5 w-5 pointer-events-none shrink-0" data-testid={`${testid}-check`} aria-label="Vybrat doklad" />}
      {children}
    </div>
  );
}

// Karta Zaplatit / Inkasovat s režimem výběru a hromadnou úhradou jedním datem (154).
const DIR = {
  out: { p: "pay", url: "/documents/payments/bulk", action: (n) => `Označit ${n} jako zaplacené`, title: "Hromadná úhrada", done: "Zaplaceno" },
  in: { p: "recv", url: "/issued-invoices/payments/bulk", action: (n) => `Označit ${n} jako inkasované`, title: "Hromadné inkaso", done: "Inkasováno" },
};

function BulkCard({ direction, title, meta, list, testid, empty, renderRow, onDone, credits = [], onRefund, onOffset }) {
  const cfg = DIR[direction];
  const [selMode, setSelMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [chip, setChip] = useState("all");
  const [dialog, setDialog] = useState(false);
  const visible = useMemo(() => (selMode ? chipFilter(list, chip) : list), [list, chip, selMode]);
  const chosen = useMemo(() => list.filter((d) => selected.has(d.id)), [list, selected]);
  const sum = chosen.reduce((s, d) => s + restOf(d), 0);

  const toggle = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allVisible = visible.length > 0 && visible.every((d) => selected.has(d.id));
  const selectVisible = () => setSelected((s) => { const n = new Set(s); visible.forEach((d) => n.add(d.id)); return n; });
  const clear = () => setSelected(new Set());
  const exitSel = () => { setSelMode(false); clear(); setChip("all"); };

  const submit = async ({ date, method }) => {
    const { data } = await api.post(cfg.url, { ids: chosen.map((d) => d.id), date, method });
    let msg = `${cfg.done} ${plur(data.paid, "doklad", "doklady", "dokladů")} · ${formatCZK(data.total)}`;
    if (data.skipped > 0) msg += ` · ${data.skipped} přeskočeno (už uhrazeno)`;
    toast.success(msg);
    setDialog(false); clear(); onDone?.();                 // zůstat ve výběru — další dávka s jiným datem
    window.dispatchEvent(new Event("pp-doc-saved"));
  };

  const bar = selected.size > 0 && (
    <div data-testid={`${cfg.p}-bulk-bar`}
      className="fixed left-3 right-3 z-30 bottom-[calc(var(--bottom-nav-h,64px)+env(safe-area-inset-bottom))] md:static md:mt-2 rounded-[14px] bg-card border border-border shadow-[0_8px_24px_rgba(0,0,0,0.12)] md:shadow-none p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
      <span className="font-bold tabular-nums" data-testid={`${cfg.p}-bulk-summary`}>{plur(selected.size, "doklad", "doklady", "dokladů")} · {formatCZK(sum)}</span>
      <Button onClick={() => setDialog(true)} className="w-full md:w-auto h-11 font-bold" data-testid={`${cfg.p}-bulk-open`}>
        <CheckCircle2 size={17} /> {cfg.action(selected.size)}
      </Button>
    </div>
  );

  return (
    <Card title={title} meta={meta} testid={testid}
      action={list.length > 0 && (
        <Button variant="outline" size="sm" onClick={() => (selMode ? exitSel() : setSelMode(true))} className="h-9 rounded-full font-bold" data-testid={`${cfg.p}-select-toggle`}>
          {selMode ? "Hotovo" : "Vybrat"}
        </Button>
      )}>
      {selMode && (
        <div className="flex items-center gap-2 flex-wrap mb-2" data-testid={`${cfg.p}-select-chips`}>
          {SEL_CHIPS.map(([v, l]) => (
            <button key={v} type="button" onClick={() => setChip(v)} data-testid={`${cfg.p}-chip-${v}`}
              className={`h-9 px-3 rounded-full text-[13px] font-bold border transition-colors ${chip === v ? "bg-brand text-white border-brand" : "bg-card border-border text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
          <button type="button" onClick={allVisible ? clear : selectVisible} data-testid={`${cfg.p}-select-all`}
            className="ml-auto text-[13px] font-bold text-brand hover:underline">
            {allVisible ? "Zrušit výběr" : `Vybrat vše (${visible.length})`}
          </button>
        </div>
      )}
      {selMode && bar}
      {list.length === 0 && credits.length === 0 && <Empty>{empty}</Empty>}
      {selMode && list.length > 0 && visible.length === 0 && <Empty>Žádný doklad neodpovídá filtru.</Empty>}
      {visible.map((d) => renderRow(d, { selMode, checked: selected.has(d.id), toggle: () => toggle(d.id) }))}
      {/* Dobropisy (167b/A): nejsou vybíratelné a nevstupují do hromadných součtů */}
      {credits.map((c) => <CreditRow key={c.id} credit={c} direction={direction} onRefund={onRefund} onOffset={onOffset} />)}
      <BulkDialog open={dialog} onClose={() => setDialog(false)} cfg={cfg} chosen={chosen} sum={sum} onSubmit={submit} />
    </Card>
  );
}

function BulkDialog({ open, onClose, cfg, chosen, sum, onSubmit }) {
  const [date, setDate] = useState(todayIso());
  const [method, setMethod] = useState("Převodem");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setDate(todayIso()); setMethod("Převodem"); } }, [open]);
  // Datum úhrady dřív než vystavení — jen upozornění, uložit se smí.
  const earlier = chosen.filter((d) => d.issue_date && date && date < d.issue_date).length;
  const confirm = async () => {
    setBusy(true);
    try { await onSubmit({ date, method }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid={`${cfg.p}-bulk-dialog`}>
        <DialogHeader>
          <DialogTitle>{cfg.title}</DialogTitle>
          <DialogDescription data-testid={`${cfg.p}-bulk-dialog-summary`}>{plur(chosen.length, "doklad", "doklady", "dokladů")} · {formatCZK(sum)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Datum úhrady</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11 mt-1 w-full min-w-0" data-testid={`${cfg.p}-bulk-date`} /></div>
          <div><Label>Způsob</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="h-11 mt-1" data-testid={`${cfg.p}-bulk-method`}><SelectValue /></SelectTrigger>
              <SelectContent>{["Převodem", "Hotově", "Kartou"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select></div>
          {earlier > 0 && (
            <p className="text-sm font-semibold text-amber-600 dark:text-amber-400" data-testid={`${cfg.p}-bulk-warn`}>
              Datum úhrady je dřív než vystavení {plur(earlier, "dokladu", "dokladů", "dokladů")}.
            </p>
          )}
        </div>
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-1">
          <Button variant="outline" onClick={onClose} className="h-12 w-full sm:w-auto font-bold" data-testid={`${cfg.p}-bulk-back`}>Zpět</Button>
          <Button onClick={confirm} disabled={busy || !date || chosen.length === 0} className="h-12 w-full sm:w-auto font-bold px-6" data-testid={`${cfg.p}-bulk-confirm`}>
            <CheckCircle2 size={18} /> Potvrdit úhradu
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Řádek dobropisu (167b/A) — vratka od dodavatele (in) / klientovi (out).
// Není vybíratelný; nabízí „Přijato/Zaplaceno" (reálná vratka) a „Započíst…".
function CreditRow({ credit, direction, onRefund, onOffset }) {
  const isIn = direction === "in";
  const name = isIn ? credit.supplier_name : credit.client_name;
  return (
    <div data-testid={`credit-${credit.id}`} className="border-b border-border last:border-b-0 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="shrink-0 bg-destructive/10 text-destructive rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide" data-testid={`credit-pill-${credit.id}`}>Dobropis</span>
          <span className="font-heading font-bold truncate">{isIn ? `Vratka od ${name || "dodavatele"}` : `Vratka klientovi ${name || ""}`.trim()}</span>
        </div>
        <div className="text-[13px] text-muted-foreground truncate">
          č. {credit.invoice_number || "—"}{credit.credited_invoice_number ? ` · k faktuře č. ${credit.credited_invoice_number}` : ""}
        </div>
      </div>
      <div className="sm:text-right shrink-0">
        <div className="font-heading font-extrabold tabular-nums text-destructive">{formatCZK(restOf(credit))}</div>
      </div>
      <div className="flex gap-2 shrink-0 w-full sm:w-auto">
        <Button size="sm" onClick={() => onRefund(credit, direction)} className="h-9 rounded-full font-bold flex-1 sm:flex-none" data-testid={`credit-refund-${credit.id}`}>{isIn ? "Přijato" : "Zaplaceno"}</Button>
        <Button size="sm" variant="outline" onClick={() => onOffset(credit)} className="h-9 rounded-full font-bold flex-1 sm:flex-none" data-testid={`credit-offset-${credit.id}`}>Započíst…</Button>
      </div>
    </div>
  );
}

// Zápočet dobropisu proti nezaplacené faktuře téže protistrany (167b/A4).
export function OffsetDialog({ offset, docs, issued, onClose, onDone }) {
  const open = !!offset;
  const credit = offset?.credit;
  const issuedColl = offset?.coll === "issued_invoices";
  const [sel, setSel] = useState(null);
  const creditRest = credit ? restOf(credit) : 0;
  const candidates = useMemo(() => {
    if (!credit) return [];
    const list = issuedColl ? issued : docs;
    const nameKey = issuedColl ? "client_name" : "supplier_name";
    const icoKey = issuedColl ? "client_ico" : "supplier_ico";
    const cIco = credit[icoKey];
    const first = (credit[nameKey] || "").toLowerCase().split(" ")[0];
    return list.filter((x) => !x.is_credit_note && x.id !== credit.id && restOf(x) >= creditRest - 0.5 && (
      (cIco && x[icoKey] && x[icoKey] === cIco) || (first.length >= 3 && (x[nameKey] || "").toLowerCase().includes(first))
    )).sort((a, b) => String(a.due_date || "9999").localeCompare(String(b.due_date || "9999")));
  }, [credit, docs, issued, issuedColl, creditRest]);
  useEffect(() => {
    if (!open || !credit) return;
    const linked = issuedColl ? credit.credits_invoice_id : credit.credits_document_id;
    setSel(linked && candidates.some((c) => c.id === linked) ? linked : (candidates[0]?.id || null));
  }, [open, credit, issuedColl, candidates]);
  const confirm = async () => {
    try {
      const url = issuedColl ? `/issued-invoices/${credit.id}/offset` : `/documents/${credit.id}/offset`;
      await api.post(url, { against_id: sel });
      toast.success(`Započteno ${formatCZK(creditRest)}.`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="offset-dialog">
        <DialogHeader>
          <DialogTitle>Započíst dobropis č. {credit?.invoice_number || "—"}</DialogTitle>
          <DialogDescription>Dobropis {formatCZK(creditRest)} se odečte od vybrané faktury. Peníze se nepohybují.</DialogDescription>
        </DialogHeader>
        {candidates.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="offset-no-candidates">Žádná nezaplacená faktura od této protistrany.</p>
        ) : (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {candidates.map((c) => (
              <button key={c.id} type="button" onClick={() => setSel(c.id)} data-testid={`offset-candidate-${c.id}`}
                className={`w-full text-left rounded-[12px] border p-3 flex items-center gap-3 transition-colors ${sel === c.id ? "border-brand bg-brand/[0.06]" : "border-border hover:bg-secondary/50"}`}>
                <span className={`w-4 h-4 rounded-full border-2 shrink-0 ${sel === c.id ? "border-brand bg-brand" : "border-muted-foreground"}`} />
                <span className="min-w-0 flex-1 text-sm">č. {c.invoice_number || "—"} · splatnost {c.due_date ? formatDate(c.due_date) : "—"} · zbývá {formatCZK(restOf(c))}</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-1">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto h-12 font-bold" data-testid="offset-close">Zavřít</Button>
          {candidates.length > 0 && (
            <Button onClick={confirm} disabled={!sel} className="w-full sm:w-auto h-12 font-bold px-6" data-testid="offset-confirm">Započíst</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SaldoBar({ saldo }) {
  const { pay, rec, payOverdue, recOverdue, balance, total } = saldo;
  const payPct = total > 0 ? (pay / total) * 100 : 0;
  return (
    <section className="bg-card border border-border rounded-[20px] p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-6 md:gap-9" data-testid="saldo-bar">
      <div className="shrink-0">
        <div className="text-[12px] uppercase tracking-widest text-muted-foreground font-bold">Saldo otevřených dokladů</div>
        <div className="font-heading font-extrabold tracking-tight text-2xl sm:text-3xl text-brand tabular-nums whitespace-nowrap mt-2">
          {balance > 0 ? "+" : balance < 0 ? "−" : ""}{formatCZK(Math.abs(balance))}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between text-sm font-bold mb-1.5">
          <span className="text-destructive tabular-nums">Zaplatit {formatCZK(pay)}</span>
          {rec > 0 && <span className="text-success tabular-nums">Inkasovat {formatCZK(rec)}</span>}
        </div>
        <div className="h-3 rounded-full overflow-hidden bg-muted flex">
          <div className="h-full bg-destructive" style={{ width: `${payPct}%` }} />
          {rec > 0 && <div className="h-full w-[2px] bg-card" />}
          {rec > 0 && <div className="h-full bg-success flex-1" />}
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground mt-1.5">
          <span className="tabular-nums">{payOverdue > 0 ? `z toho ${formatCZK(payOverdue)} po splatnosti` : "nic po splatnosti"}</span>
          {rec > 0 && <span className="tabular-nums">{recOverdue > 0 ? `z toho ${formatCZK(recOverdue)} po splatnosti` : ""}</span>}
        </div>
      </div>
    </section>
  );
}

const MONTHS = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];
const DOWS = ["Pondělí", "Úterý", "Středa", "Čtvrtek", "Pátek", "Sobota", "Neděle"];

// Měsíční mřížka plateb. Týden začíná pondělím (ne nedělí jako v US).
export function calendarCells(month) {
  const [y, m] = month.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const shift = (first.getDay() + 6) % 7;          // po=0 … ne=6
  const start = new Date(y, m - 1, 1 - shift);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    cells.push({ iso, day: d.getDate(), inMonth: d.getMonth() === m - 1 });
  }
  // poslední týden zahodíme, když do zobrazeného měsíce nezasahuje
  return cells.slice(0, cells.slice(35).some((c) => c.inMonth) ? 42 : 35);
}

function PaymentsCalendar({ month, setMonth, payments }) {
  const navigate = useNavigate();
  const cells = useMemo(() => calendarCells(month), [month]);
  const byDay = useMemo(() => {
    const m = {};
    payments.forEach((p) => { (m[p.date] = m[p.date] || []).push(p); });
    return m;
  }, [payments]);
  const inMonth = payments.filter((p) => (p.date || "").startsWith(month));
  const out = inMonth.filter((p) => p.direction === "out" && !p.isOffset).reduce((s, p) => s + p.amount, 0);
  const inc = inMonth.filter((p) => p.direction === "in" && !p.isOffset).reduce((s, p) => s + p.amount, 0);
  const shift = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };
  const [y, m] = month.split("-").map(Number);

  return (
    <section className="bg-card border border-border rounded-[20px] p-5" data-testid="payments-calendar">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shift(-1)} className="w-[38px] h-[38px] rounded-full" data-testid="pay-calendar-prev"><ChevronLeft size={17} /></Button>
          <Button variant="outline" size="icon" onClick={() => shift(1)} className="w-[38px] h-[38px] rounded-full" data-testid="pay-calendar-next"><ChevronRight size={17} /></Button>
          <h2 className="font-heading font-extrabold text-[20px] ml-1">{MONTHS[m - 1]} {y}</h2>
        </div>
        <div className="text-sm text-muted-foreground tabular-nums">
          Odchozí <b className="text-foreground">{formatCZK(out)}</b> · Příchozí <b className="text-foreground">{formatCZK(inc)}</b> · Saldo <b className="text-brand">{formatCZK(inc - out)}</b>
        </div>
      </div>

      {inMonth.length === 0 && <p className="text-sm text-muted-foreground mb-3">V tomto měsíci není zaznamenaná žádná platba.</p>}

      <div className="overflow-x-auto">
        <div className="min-w-[700px]">
          <div className="grid grid-cols-7 bg-muted/40 rounded-t-[10px]">
            {DOWS.map((d) => <div key={d} className="px-2 py-2 text-[12px] font-bold uppercase text-muted-foreground truncate">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 border-l border-t border-border rounded-b-[10px] overflow-hidden">
            {cells.map((c) => (
              <div key={c.iso} className={`min-h-[118px] p-2 border-r border-b border-border ${c.inMonth ? "" : "bg-muted/30"}`} data-testid={`cal-day-${c.iso}`}>
                <div className={`text-xs font-bold mb-1 tabular-nums ${c.inMonth ? "text-muted-foreground" : "text-muted-foreground/40"}`}>{c.day}</div>
                <div className="space-y-1">
                  {(byDay[c.iso] || []).map((p) => (
                    <button key={`${p.document_id}-${p.id}`} type="button" title={p.document_label} data-testid={`cal-payment-${p.id}`}
                      onClick={() => navigate(`/ekonomika/doklady?open=${p.direction === "in" ? "vydana" : "faktura"}:${p.document_id}`)}
                      className={`w-full text-left rounded-[8px] p-1.5 transition-shadow hover:ring-2 hover:ring-brand/40 ${p.isOffset ? "bg-secondary" : p.direction === "in" ? "bg-success/10" : "bg-destructive/[0.06]"}`}>
                      <div className="font-heading font-extrabold text-[12px] tabular-nums">{p.isOffset ? "" : p.direction === "in" ? "+" : "−"}{formatCZK(p.amount)}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{p.counterparty}{p.invoice_number ? ` · č. ${p.invoice_number}` : ""}</div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
